import { useState } from "react";

const QUICK_ACTIONS = [
  { id: "shorter", label: "더 짧게 (핵심 발언만)", icon: "ri-scissors-2-line", color: "#00D9FF" },
  { id: "hook", label: "첫 발언을 훅으로 배치", icon: "ri-flashlight-line", color: "#F59E0B" },
  { id: "speaker", label: "발언자 클로즈업 포커스", icon: "ri-user-focus-line", color: "#10B981" },
  { id: "remove_low", label: "침묵·망설임 구간 제거", icon: "ri-eraser-line", color: "#EF4444" },
];

const SUBTITLE_LINES = [
  { id: 1, time: "0:00", text: "종교에서 어떻게 알고 나왔어요?" },
  { id: 2, time: "0:03", text: "먼저 알게 된 계기는" },
  { id: 3, time: "0:07", text: "유튜브에서 본 종교의 실체" },
  { id: 4, time: "0:10", text: "알고나서는 쉽게 나올 수 있었어?" },
];

const SUBTITLE_STYLES = [
  { id: "bold", label: "굵게", preview: "font-bold text-white" },
  { id: "highlight", label: "하이라이트", preview: "font-bold text-[#F59E0B]" },
  { id: "shadow", label: "그림자", preview: "font-semibold text-white [text-shadow:0_2px_8px_rgba(0,0,0,0.8)]" },
];

export default function AIAssistPanel() {
  const [activeAction, setActiveAction] = useState<string | null>(null);
  const [subtitleStyle, setSubtitleStyle] = useState("bold");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [subtitles, setSubtitles] = useState(SUBTITLE_LINES);
  const [processingAction, setProcessingAction] = useState<string | null>(null);

  const handleAction = (id: string) => {
    setActiveAction(id);
    setProcessingAction(id);
    setTimeout(() => setProcessingAction(null), 2000);
  };

  return (
    <div className="h-full flex flex-col gap-4 overflow-y-auto" style={{ scrollbarWidth: "none" }}>
      {/* Section label */}
      <div className="flex items-center gap-2">
        <div className="w-6 h-6 flex items-center justify-center rounded-lg bg-[#00D9FF]/15">
          <i className="ri-brain-line text-[#00D9FF] text-sm" />
        </div>
        <span className="text-white text-sm font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          AI Assist
        </span>
      </div>

      {/* Clip summary */}
      <div className="bg-[#0F1419] border border-white/8 rounded-xl p-4">
        <p className="text-gray-400 text-xs font-medium uppercase tracking-wider mb-2" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
          클립 선정 이유
        </p>
        <p className="text-gray-300 text-xs leading-relaxed mb-3" style={{ fontFamily: "'Inter', sans-serif" }}>
          출연자가 종교 탈출 계기를 처음 언급하는 순간으로, 표정 변화와 발화 속도 급감이 감지됐습니다.
          진술 내용의 공감 유발 강도 및 주제 희소성이 높아 숏폼 바이럴 가능성이 매우 높은 구간입니다.
        </p>
        <div className="grid grid-cols-2 gap-2">
          <div className="bg-white/4 rounded-lg p-2.5">
            <div className="text-[#00D9FF] text-base font-bold" style={{ fontFamily: "'JetBrains Mono', monospace" }}>9.7</div>
            <div className="text-gray-500 text-xs mt-0.5" style={{ fontFamily: "'Inter', sans-serif" }}>바이럴 점수</div>
          </div>
          <div className="bg-white/4 rounded-lg p-2.5">
            <div className="text-[#F59E0B] text-base font-bold" style={{ fontFamily: "'JetBrains Mono', monospace" }}>공감 유발</div>
            <div className="text-gray-500 text-xs mt-0.5" style={{ fontFamily: "'Inter', sans-serif" }}>콘텐츠 강도</div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div>
        <p className="text-gray-400 text-xs font-medium uppercase tracking-wider mb-2.5" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
          빠른 편집
        </p>
        <div className="space-y-2">
          {QUICK_ACTIONS.map((action) => (
            <button
              key={action.id}
              onClick={() => handleAction(action.id)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border transition-all cursor-pointer text-left ${
                activeAction === action.id
                  ? "border-white/20 bg-white/8"
                  : "border-white/6 bg-[#0F1419] hover:border-white/14 hover:bg-white/5"
              }`}
            >
              <div
                className="w-7 h-7 flex items-center justify-center rounded-lg shrink-0"
                style={{ background: `${action.color}18` }}
              >
                {processingAction === action.id ? (
                  <i className="ri-loader-4-line text-sm animate-spin" style={{ color: action.color }} />
                ) : (
                  <i className={`${action.icon} text-sm`} style={{ color: action.color }} />
                )}
              </div>
              <span className="text-white text-xs font-medium" style={{ fontFamily: "'Inter', sans-serif" }}>
                {action.label}
              </span>
              {processingAction === action.id && (
                <span className="ml-auto text-xs text-gray-500" style={{ fontFamily: "'JetBrains Mono', monospace" }}>처리 중...</span>
              )}
              {activeAction === action.id && processingAction !== action.id && (
                <span className="ml-auto w-4 h-4 flex items-center justify-center rounded-full bg-[#10B981]/20">
                  <i className="ri-check-line text-[#10B981] text-xs" />
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Subtitle Controls */}
      <div>
        <div className="flex items-center justify-between mb-2.5">
          <p className="text-gray-400 text-xs font-medium uppercase tracking-wider" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
            자막 편집
          </p>
          <div className="flex items-center gap-1">
            {SUBTITLE_STYLES.map((s) => (
              <button
                key={s.id}
                onClick={() => setSubtitleStyle(s.id)}
                className={`px-2 py-1 rounded-md text-xs transition-all cursor-pointer whitespace-nowrap ${
                  subtitleStyle === s.id
                    ? "bg-white/12 text-white"
                    : "text-gray-500 hover:text-gray-300"
                }`}
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
        <div className="space-y-1.5 max-h-44 overflow-y-auto" style={{ scrollbarWidth: "none" }}>
          {subtitles.map((sub) => (
            <div
              key={sub.id}
              className="group flex items-start gap-2 bg-[#0F1419] border border-white/6 rounded-lg px-3 py-2 hover:border-white/14 transition-all"
            >
              <span
                className="text-gray-600 text-xs shrink-0 mt-0.5"
                style={{ fontFamily: "'JetBrains Mono', monospace" }}
              >
                {sub.time}
              </span>
              {editingId === sub.id ? (
                <input
                  autoFocus
                  value={sub.text}
                  onChange={(e) =>
                    setSubtitles((prev) =>
                      prev.map((s) => (s.id === sub.id ? { ...s, text: e.target.value } : s))
                    )
                  }
                  onBlur={() => setEditingId(null)}
                  className="flex-1 bg-transparent text-white text-xs outline-none border-b border-[#00D9FF]/50"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                />
              ) : (
                <span
                  className="flex-1 text-gray-300 text-xs leading-relaxed cursor-text"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                  onClick={() => setEditingId(sub.id)}
                >
                  {sub.text}
                </span>
              )}
              <button
                onClick={() => setEditingId(sub.id)}
                className="opacity-0 group-hover:opacity-100 w-5 h-5 flex items-center justify-center text-gray-500 hover:text-white transition-all cursor-pointer"
              >
                <i className="ri-edit-line text-xs" />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
